import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { 
  getSubscriptionStatus, 
  createCheckoutSession, 
  createPortalSession,
  PLAN_CONFIGS,
  getPlanFeatures,
  calculatePlanPrice,
  formatPrice,
  getPlanDisplayName,
  getUpgradeRecommendation,
  type SubscriptionStatus
} from '../lib/billing'
import { CreditCard, Crown, Zap, Users, Check, ArrowUpRight } from 'lucide-react'

interface BillingManagementProps {
  teamId: string
  userRole?: string
  isOpen?: boolean
  onClose?: () => void
  initialPlan?: string | null
}

export const BillingManagement: React.FC<BillingManagementProps> = ({ 
  teamId, 
  userRole = 'admin', 
  isOpen = false, 
  onClose, 
  initialPlan 
}) => {
  const { user } = useAuth()
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [upgrading, setUpgrading] = useState<string | null>(null)

  const isAdmin = userRole === 'admin'

  useEffect(() => {
    if (user && isAdmin) {
      loadSubscriptionStatus()
    } else {
      setLoading(false)
    }
  }, [user, teamId, isAdmin])

  // Poll for subscription updates after checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const billingStatus = urlParams.get('billing')
    
    if (billingStatus === 'success' && user && isAdmin) {
      // User returned from successful checkout, start polling
      console.log('🔄 User returned from checkout success, starting polling...')
      
      let pollCount = 0
      const maxPolls = 20 // Poll for up to 2 minutes
      
      const pollInterval = setInterval(async () => {
        pollCount++
        console.log(`🔄 Polling attempt ${pollCount}/${maxPolls}`)
        
        try {
          await loadSubscriptionStatus()
          
          // Check if subscription has been updated
          if (subscriptionStatus?.subscription?.status && 
              ['active', 'trialing'].includes(subscriptionStatus.subscription.status)) {
            console.log('✅ Subscription status updated!')
            clearInterval(pollInterval)
            // Clean up URL
            const newUrl = window.location.pathname + window.location.hash
            window.history.replaceState({}, '', newUrl)
          }
        } catch (error) {
          console.error('Error during polling:', error)
        }
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval)
          console.log('⏰ Polling timeout reached')
        }
      }, 6000) // Poll every 6 seconds
      
      // Cleanup on unmount
      return () => clearInterval(pollInterval)
    }
  }, [user, isAdmin, teamId])

  // Auto-upgrade if initialPlan is provided
  useEffect(() => {
    if (initialPlan && subscriptionStatus && !upgrading) {
      handleUpgrade(initialPlan)
    }
  }, [initialPlan, subscriptionStatus, upgrading])

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true)
      setError('')
      const status = await getSubscriptionStatus(teamId, user!.id)
      setSubscriptionStatus(status)
    } catch (err: any) {
      console.error('Error loading subscription:', err)
      setError(err.message || 'Failed to load subscription details')
    } finally {
      setLoading(false)
    }
  }

  const handleUpgrade = async (planName: string) => {
    if (!user) return

    console.log('=== UPGRADE DEBUG ===')
    console.log('Team ID:', teamId)
    console.log('Plan Name:', planName)
    console.log('User ID:', user.id)
    console.log('User Email:', user.email)
    console.log('=====================')

    try {
      setUpgrading(planName)
      setError('')
      const checkoutUrl = await createCheckoutSession(teamId, planName, user.id)
      window.location.href = checkoutUrl
    } catch (err: any) {
      console.error('Error creating checkout:', err)
      setError(err.message || 'Failed to start upgrade process')
      setUpgrading(null)
    }
  }

  const handleManageBilling = async () => {
    if (!user) return

    console.log('=== BILLING DEBUG ===')
    console.log('Team ID:', teamId)
    console.log('User ID:', user.id)
    console.log('User Email:', user.email)
    console.log('=====================')

    try {
      setError('')
      const portalUrl = await createPortalSession(teamId, user.id)
      window.open(portalUrl, '_blank')
    } catch (err: any) {
      console.error('Error opening billing portal:', err)
      
      if (err.name === 'SubscriptionRequiredError' || err.needsSubscription) {
        setError(`You need to subscribe to a plan first to access billing management. Your current plan is ${getPlanDisplayName(err.teamPlan || currentPlan)}. Please choose a plan below to get started.`)
      } else if (err.message?.includes('No configuration provided')) {
        setError('Billing portal is being set up. Please use the subscription options below to manage your plan.')
      } else {
        setError(err.message || 'Failed to open billing portal')
      }
    }
  }

  const handleCancelSubscription = async () => {
    if (!user || !confirm('Are you sure you want to cancel your subscription? You will lose access to paid features at the end of your billing period.')) return

    try {
      setError('')
      setUpgrading('cancel')
      const portalUrl = await createPortalSession(teamId, user.id)
      window.open(portalUrl, '_blank')
    } catch (err: any) {
      console.error('Error opening billing portal:', err)
      if (err.message?.includes('No configuration provided')) {
        setError('Billing portal is being set up. Please contact support to cancel your subscription.')
      } else {
        setError(err.message || 'Failed to open billing portal')
      }
    } finally {
      setUpgrading(null)
    }
  }

  if (!isAdmin) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="text-sm text-yellow-800">
          Only team administrators can manage billing settings.
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  const currentPlan = subscriptionStatus?.subscription?.plan_name || subscriptionStatus?.team_plan || 'trial'
  
  // Debug logging
  console.log('=== BILLING DEBUG ===')
  console.log('Full Subscription Status:', JSON.stringify(subscriptionStatus, null, 2))
  console.log('Subscription object:', subscriptionStatus?.subscription)
  console.log('Team plan fallback:', subscriptionStatus?.team_plan)
  console.log('Calculated current plan:', currentPlan)
  console.log('Subscription status:', subscriptionStatus?.subscription?.status)
  console.log('Plan name from subscription:', subscriptionStatus?.subscription?.plan_name)
  console.log('=== END DEBUG ===')  
  
  const currentFeatures = getPlanFeatures(currentPlan)
  const memberCount = subscriptionStatus?.member_count || 0
  const recommendedPlan = getUpgradeRecommendation(currentPlan, memberCount)
  const hasActiveSubscription = subscriptionStatus?.subscription?.status === 'active'

  const content = (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Current Plan Status */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
          {/* Show manage billing button - error handling will guide users to subscribe if needed */}
          <button
            onClick={handleManageBilling}
            className="flex items-center space-x-2 text-sm text-indigo-600 hover:text-indigo-700"
          >
            <CreditCard className="h-4 w-4" />
            <span>Manage Billing</span>
            <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>

        <div className="flex items-center space-x-3 mb-4">
          <div className={`p-2 rounded-lg ${
            currentPlan === 'trial' ? 'bg-gray-100' :
            currentPlan === 'advanced_trial' ? 'bg-purple-100' :
            currentPlan === 'essentials' ? 'bg-blue-100' :
            'bg-purple-100'
          }`}>
            {currentPlan === 'trial' ? <Users className="h-5 w-5 text-gray-600" /> :
             currentPlan === 'advanced_trial' ? <Zap className="h-5 w-5 text-purple-600" /> :
             currentPlan === 'essentials' ? <Crown className="h-5 w-5 text-blue-600" /> :
             <Zap className="h-5 w-5 text-purple-600" />}
          </div>
          <div>
            <h4 className="font-medium text-gray-900">
              {getPlanDisplayName(currentPlan)}
              {(subscriptionStatus?.subscription?.status === 'trialing' || 
                (subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date()) ||
                ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) 
                && ' (Free Trial)'}
            </h4>
            <p className="text-sm text-gray-500">
              {memberCount} of {
                typeof currentFeatures.maxUsers === 'number' 
                  ? currentFeatures.maxUsers 
                  : '∞'
              } users
            </p>
          </div>
        </div>

        {(subscriptionStatus?.subscription || ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`ml-2 font-medium ${
                  (subscriptionStatus?.subscription?.status === 'active' || 
                   subscriptionStatus?.subscription?.status === 'trialing' ||
                   ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date() 
                    ? 'trialing' 
                    : subscriptionStatus?.subscription?.status || (['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '') ? 'trialing' : 'inactive')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">
                  {(subscriptionStatus?.subscription?.status === 'trialing' || 
                    (subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date()) ||
                    ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) 
                    ? 'Trial ends:' : 'Next billing:'}
                </span>
                <span className="ml-2 font-medium text-gray-900">
                  {subscriptionStatus?.subscription?.current_period_end 
                    ? new Date(subscriptionStatus?.subscription?.current_period_end).toLocaleDateString()
                    : (subscriptionStatus?.team_created_at && ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || ''))
                      ? (() => {
                          const trialEnd = new Date(subscriptionStatus.team_created_at)
                          trialEnd.setDate(trialEnd.getDate() + 7)
                          return trialEnd.toLocaleDateString()
                        })()
                      : 'Date not set'}
                </span>
              </div>
            </div>
            {subscriptionStatus?.subscription?.cancel_at_period_end && (
              <div className="mt-2 text-sm text-yellow-600">
                Your subscription will cancel at the end of the current period.
              </div>
            )}
          </div>
        )}

        {/* Cancel Subscription Button - only show for active subscriptions */}
        {hasActiveSubscription && !subscriptionStatus?.subscription?.cancel_at_period_end && (
          <div className="flex justify-end">
            <button
              onClick={handleCancelSubscription}
              disabled={upgrading === 'cancel'}
              className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
            >
              {upgrading === 'cancel' ? 'Processing...' : 'Cancel Subscription'}
            </button>
          </div>
        )}

        {/* Current Plan Features */}
        <div className="space-y-2">
          <h5 className="font-medium text-gray-700">Current Features</h5>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center space-x-2 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <span>
                Up to {
                  typeof currentFeatures.maxUsers === 'number' 
                    ? currentFeatures.maxUsers 
                    : 'unlimited'
                } team members
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              {currentFeatures.dailySummaries ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <span className="h-4 w-4 rounded-full border-2 border-gray-300" />
              )}
              <span className={currentFeatures.dailySummaries ? '' : 'text-gray-400'}>
                AI-powered daily summaries
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              {currentFeatures.advancedAnalytics ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <span className="h-4 w-4 rounded-full border-2 border-gray-300" />
              )}
              <span className={currentFeatures.advancedAnalytics ? '' : 'text-gray-400'}>
                Advanced analytics & insights
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Recommendation */}
      {recommendedPlan && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Crown className="h-5 w-5 text-amber-600 mt-0.5" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">Upgrade Recommended</h4>
              <p className="text-sm text-amber-700 mt-1">
                You have {memberCount} team members. Consider upgrading to {getPlanDisplayName(recommendedPlan)} 
                for {recommendedPlan === 'essentials' ? 'up to 4 users' : 'unlimited users'} and additional features.
              </p>
              <button
                onClick={() => handleUpgrade(recommendedPlan)}
                disabled={upgrading === recommendedPlan}
                className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {upgrading === recommendedPlan ? 'Processing...' : `Upgrade to ${getPlanDisplayName(recommendedPlan)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Options - only show if on trial or can upgrade to different plan */}
      {(currentPlan === 'trial' || currentPlan === 'advanced_trial' || !hasActiveSubscription) && (
        <>
          {(currentPlan === 'trial' || currentPlan === 'advanced_trial') && (
            <div className="mb-4 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Plan</h3>
              <p className="text-sm text-gray-600">
                You're currently on a free trial. Select a plan to continue after your trial ends.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(PLAN_CONFIGS)
            .filter(([name]) => !['trial', 'advanced_trial'].includes(name) && name !== currentPlan)
            .map(([planName, features]) => (
            <div key={planName} className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2 rounded-lg ${
                  planName === 'essentials' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {planName === 'essentials' ? 
                    <Crown className="h-5 w-5 text-blue-600" /> :
                    <Zap className="h-5 w-5 text-purple-600" />
                  }
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{getPlanDisplayName(planName)}</h4>
                  <p className="text-sm text-gray-500">
                    {formatPrice(features.price)}{features.pricePerUser ? '/user' : ''}/month
                    {features.pricePerUser && memberCount > 1 && (
                      <span className="text-xs text-gray-400 block">
                        {formatPrice(calculatePlanPrice(planName, memberCount))} total for {memberCount} users
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex items-center space-x-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>
                    Up to {
                      typeof features.maxUsers === 'number' 
                        ? features.maxUsers 
                        : 'unlimited'
                    } team members
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>AI-powered daily summaries</span>
                </div>
                {features.advancedAnalytics && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>Advanced analytics & insights</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleUpgrade(planName)}
                disabled={upgrading === planName}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  planName === 'essentials' 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                } disabled:opacity-50`}
              >
                {upgrading === planName ? 'Processing...' : 
                  (currentPlan === 'trial' || currentPlan === 'advanced_trial') 
                    ? `Upgrade to ${getPlanDisplayName(planName)}`
                    : `Start ${getPlanDisplayName(planName)} Trial`
                }
              </button>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  )

  // Return modal version if isOpen is true
  if (isOpen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Billing & Subscription</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {content}
        </div>
      </div>
    )
  }

  return content
}